import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  closestCenter, MeasuringStrategy,
  type DragEndEvent,
  type DragStartEvent,
  type Modifier,
} from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../lib/api";
import { useHotkey } from "../lib/keybindings";
import { useRenameRequest } from "../lib/renameRequest";

interface Props {
  // Namespaced tab IDs: `r:<requestId>`, `w:<webhookId>`, or
  // `f:<flowId>`. Legacy bare IDs (no prefix) are treated as request
  // IDs so older URLs still work.
  tabs: string[];
  activeId: string | null;
  onClose: (id: string) => void;
  onReorder: (next: string[]) => void;
}

// Split a tab ID into its kind + raw id. Bare IDs default to request.
function parseTabId(id: string): { kind: "request" | "webhook" | "flow"; rawId: string } {
  if (id.startsWith("w:")) return { kind: "webhook", rawId: id.slice(2) };
  if (id.startsWith("r:")) return { kind: "request", rawId: id.slice(2) };
  if (id.startsWith("f:")) return { kind: "flow", rawId: id.slice(2) };
  return { kind: "request", rawId: id };
}

function pathForTabId(id: string): string {
  const { kind, rawId } = parseTabId(id);
  if (kind === "webhook") return `/w/${rawId}`;
  if (kind === "flow") return `/f/${rawId}`;
  return `/r/${rawId}`;
}

// Modeled on the dnd-kit "horizontal sortable" basic-setup story: a single
// SortableContext + horizontalListSortingStrategy, paired with a
// DragOverlay so the dragged pill follows the cursor smoothly without
// being clipped by the strip's overflow.
const restrictToHorizontalAxis: Modifier = ({ transform }) => ({ ...transform, y: 0 });

export default function TabStrip({ tabs, activeId, onClose, onReorder }: Props) {
  const nav = useNavigate();
  const [dragId, setDragId] = useState<string | null>(null);

  useHotkey("Shift+Tab", () => {
    if (tabs.length < 2) return;
    const idx = activeId ? tabs.indexOf(activeId) : -1;
    const next = tabs[(idx + 1) % tabs.length];
    if (next) {
      nav({ pathname: pathForTabId(next), search: window.location.search });
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
  });

  useHotkey("Mod+Shift+w", () => {
    if (!activeId) return;
    const idx = tabs.indexOf(activeId);
    if (idx < 0) return;
    const remaining = tabs.filter((t) => t !== activeId);
    onClose(activeId);
    if (remaining.length === 0) { nav({ pathname: "/", search: window.location.search }); return; }
    const neighbour = remaining[Math.min(idx, remaining.length - 1)];
    nav({ pathname: pathForTabId(neighbour), search: window.location.search });
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = navigator.platform.match(/Mac|iPhone|iPad/) ? e.metaKey : e.ctrlKey;
      if (!mod || e.shiftKey || e.altKey) return;
      const n = parseInt(e.key, 10);
      if (!Number.isFinite(n) || n < 1 || n > 9) return;
      const target = tabs[n - 1];
      if (target) {
        e.preventDefault();
        nav({ pathname: pathForTabId(target), search: window.location.search });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [tabs, nav]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  if (tabs.length === 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      // Always-measure ensures the rects stay current as the strip scrolls.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={(e: DragStartEvent) => setDragId(String(e.active.id))}
      onDragCancel={() => setDragId(null)}
      onDragEnd={(e: DragEndEvent) => {
        setDragId(null);
        if (!e.over || e.active.id === e.over.id) return;
        const from = tabs.indexOf(String(e.active.id));
        const to = tabs.indexOf(String(e.over.id));
        if (from < 0 || to < 0) return;
        onReorder(arrayMove(tabs, from, to));
      }}
    >
      <SortableContext items={tabs} strategy={horizontalListSortingStrategy}>
        <div className="h-9 flex items-stretch overflow-x-auto scroll-pane border-b border-border bg-surface">
          {tabs.map((id) => (
            <SortableTabPill
              key={id}
              tabId={id}
              active={id === activeId}
              onSelect={() => nav({ pathname: pathForTabId(id), search: window.location.search })}
              onClose={() => {
                const idx = tabs.indexOf(id);
                const remaining = tabs.filter((t) => t !== id);
                onClose(id);
                if (id === activeId) {
                  const search = window.location.search;
                  if (remaining.length === 0) nav({ pathname: "/", search });
                  else nav({ pathname: pathForTabId(remaining[Math.min(idx, remaining.length - 1)]), search });
                }
              }}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" }}>
        {dragId ? <TabPillOverlay tabId={dragId} active={dragId === activeId} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

// Per-tab data hook. Dispatches the lookup by tab kind so a single
// strip can display request + webhook + flow pills together.
// staleTime: 60s keeps the strip cheap during heavy navigation.
function useTabSummary(tabId: string) {
  const { kind, rawId } = parseTabId(tabId);
  const reqQ = useQuery({
    queryKey: ["request", rawId],
    queryFn: () => api.requests.get(rawId),
    staleTime: 60_000,
    enabled: kind === "request",
  });
  const whQ = useQuery({
    queryKey: ["webhook", rawId],
    queryFn: () => api.webhooks.get(rawId),
    staleTime: 60_000,
    enabled: kind === "webhook",
  });
  const flQ = useQuery({
    queryKey: ["flow", rawId],
    queryFn: () => api.flows.get(rawId),
    staleTime: 60_000,
    enabled: kind === "flow",
  });
  if (kind === "webhook") {
    const w = whQ.data?.webhook;
    return {
      kind: "webhook" as const,
      name: w?.name ?? "Loading…",
      method: undefined as string | undefined,
      url: w?.url,
      rawId,
    };
  }
  if (kind === "flow") {
    const f = flQ.data?.flow;
    return {
      kind: "flow" as const,
      name: f?.name ?? "Loading…",
      method: undefined as string | undefined,
      url: undefined as string | undefined,
      rawId,
    };
  }
  const r = reqQ.data?.request;
  return {
    kind: "request" as const,
    name: r?.name ?? "Loading…",
    method: r?.method,
    url: r?.url,
    rawId,
  };
}

// Branch icon used for flow pills — distinct from the webhook bolt
// and the request method-tag.
function FlowPillIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 4v6a4 4 0 0 0 4 4h8M18 14l-3-3M18 14l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SortableTabPill({ tabId, active, onSelect, onClose }: { tabId: string; active: boolean; onSelect: () => void; onClose: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tabId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };
  const s = useTabSummary(tabId);
  const rename = useRenameRequest();
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={
        "shrink-0 flex items-center gap-2 px-3 border-r border-border text-sm cursor-pointer select-none outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft relative " +
        (active
          ? "bg-bg text-text border-b-2 border-b-accent"
          : "text-text-dim hover:text-text hover:bg-surface-2 border-b-2 border-b-transparent")
      }
      onClick={onSelect}
      onAuxClick={(e) => { if (e.button === 1) onClose(); }}
      title={`${s.kind === "webhook" ? "Webhook" : s.kind === "flow" ? "Flow" : (s.method ?? "GET")} ${s.url ?? ""}${s.kind === "request" ? " — double-click to rename" : ""}`}
    >
      {s.kind === "webhook" ? (
        <span className="text-accent" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></svg>
        </span>
      ) : s.kind === "flow" ? (
        <span className="text-accent" aria-hidden="true"><FlowPillIcon /></span>
      ) : (
        <span className={"method-tag " + (s.method ?? "GET").toLowerCase()}>{(s.method ?? "GET").toUpperCase()}</span>
      )}
      <span
        className="max-w-[180px] truncate"
        onDoubleClick={(e) => {
          e.stopPropagation();
          // Rename is wired for requests only; webhooks rename inside the
          // builder (and the sidebar's row menu).
          if (s.kind === "request") rename(s.rawId, s.name);
        }}
      >{s.name}</span>
      <button
        type="button"
        aria-label="Close tab"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="h-5 w-5 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-3"
      >×</button>
    </div>
  );
}

function TabPillOverlay({ tabId, active }: { tabId: string; active: boolean }) {
  const s = useTabSummary(tabId);
  return (
    <div
      className={
        "shrink-0 flex items-center gap-2 px-3 py-1.5 border-r border-border text-sm cursor-grabbing select-none shadow-[var(--shadow-strong)] " +
        (active ? "bg-bg text-text" : "bg-surface text-text-dim")
      }
    >
      {s.kind === "webhook" ? (
        <span className="text-accent" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></svg>
        </span>
      ) : s.kind === "flow" ? (
        <span className="text-accent" aria-hidden="true"><FlowPillIcon /></span>
      ) : (
        <span className={"method-tag " + (s.method ?? "GET").toLowerCase()}>{(s.method ?? "GET").toUpperCase()}</span>
      )}
      <span className="max-w-[180px] truncate">{s.name}</span>
    </div>
  );
}
